import { useState, useMemo, useEffect, type JSX } from "react";
import { useTable } from "react-table";
import { useAppSelector } from "./hooks/reduxHooks";
import { buildDealColumns } from "./components/deals/dealColumns";
import DealsTableGrid from "./components/deals/DealsTableGrid";
import DealDetailsModal from "./components/deals/DealDetailsModal";
import DealRationaleModal from "./components/deals/DealRationaleModal";
import type { DealData, DocumentData } from "./components/deals/dealTypes";

/** DEBUG: log the deals the table is asked to render. */
function useDealsDebugLog(activeDeals: DealData[]): void {
  useEffect(() => {
    if (activeDeals.length === 0) return;

    console.log("🔍 [DealsTable] Rendering deals, count:", activeDeals.length);
    const sample = activeDeals[0];
    console.log("🔍 [DealsTable] Sample deal for table:", {
      dealName: sample.dealName,
      year: sample.year,
      investmentYear: sample.investmentYear,
      stage: sample.stage,
      dealStage: sample.dealStage,
      team: sample.team,
      description: sample.description,
      allKeys: Object.keys(sample),
    });
  }, [activeDeals]);
}

/** Search results as a table of deals, each row opening a details modal. */
export default function DealsTable_Component(): JSX.Element {
  const [selectedDeal, setSelectedDeal] = useState<DealData | null>(null);
  const [rationaleModalDeal, setRationaleModalDeal] = useState<DealData | null>(null);

  // Selectors for deals and documents from results slice
  const activeDeals = useAppSelector((state) => state.resultsSlice.deals || []) as DealData[];
  const status = useAppSelector((state) => state.resultsSlice.status);
  const availableDocs = useAppSelector(
    (state) => state.resultsSlice.documents || []
  ) as DocumentData[];

  useDealsDebugLog(activeDeals);

  const columns = useMemo(
    () => buildDealColumns(availableDocs, setRationaleModalDeal),
    [availableDocs]
  );

  const table = useTable<DealData>({
    columns,
    data: activeDeals,
    getRowId: (row) => row.dealName,
  });

  return (
    <>
      <DealsTableGrid
        table={table}
        isLoading={status === "loading"}
        columnCount={columns.length}
        onRowClick={setSelectedDeal}
      />
      <DealDetailsModal
        key={selectedDeal?.dealId ?? ""}
        deal={selectedDeal}
        onClose={() => setSelectedDeal(null)}
      />
      <DealRationaleModal
        deal={rationaleModalDeal}
        onClose={() => setRationaleModalDeal(null)}
      />
    </>
  );
}
